import { Header } from "../components/landing/Header";
import { Footer } from "../components/landing/Footer";
import { Link } from "react-router-dom";
import { useSeo } from "../hooks/useSeo";

export default function TermsPage() {
  useSeo({
    title: "Terms & Conditions | iQ Hub",
    description:
      "Read the official Terms & Conditions governing participation in iQ Hub events organized by IQ International Pte. Ltd.",
    ogTitle: "Terms & Conditions | iQ Hub",
    ogDescription:
      "Read the official Terms & Conditions governing participation in iQ Hub events organized by IQ International Pte. Ltd.",
    ogUrl: "https://www.iq-hub.com/terms-and-conditions",
    canonical: "https://www.iq-hub.com/terms-and-conditions",
  });

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-8">
            Terms & Conditions
          </h1>
          
          <div className="prose prose-lg max-w-none text-[#475569]">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">1. Scope of Agreement</h2>
              <p>
                This legally binding Agreement ("Agreement") is hereby executed and effective between the Attendee and IQ International Pte. Ltd ("Organizer"), collectively referred to as the "Parties," and governs the comprehensive terms and conditions governing the provision and utilization of services, including, but not limited to, the Attendee's participation in IQ Hub events. Any capitalized terms not explicitly defined herein shall be construed as per their definitions set forth during the registration process.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">2. Definition of 'Attendee'</h2>
              <p className="mb-4">
                For the purposes of this Agreement, the term 'Attendee' encompasses all individuals, groups, entities, or organizations participating in Events, irrespective of their payment status. This all-encompassing definition includes those who have fulfilled payments, those with outstanding payments, and those attending on a complimentary basis. Distinct categories of Attendees include, but are not limited to:
              </p>
              <p className="mb-2">
                <strong>a) Participants:</strong> All individuals or entities that have registered for an Event. Participants may engage in various activities including attending sessions, participating in workshops, and availing themselves of networking opportunities.
              </p>
              <p className="mb-2">
                <strong>b) Exhibitors and Sponsors:</strong> Entities or individuals with a commercial or promotional role at Events. Exhibitors typically showcase products or services in designated areas, while sponsors may provide financial or service-based support, often in exchange for marketing privileges.
              </p>
              <p className="mb-2">
                <strong>c) Speakers, Panel Members, and Moderators:</strong> Individuals engaged in delivering talks, presentations, or seminars; members participating in panel discussions; moderators overseeing discussions or sessions; event hosts responsible for presenting or supervising various segments; and any individuals performing on stage or in front of the audience in any capacity.
              </p>
              <p className="mb-2">
                <strong>d) Media Representatives:</strong> Journalists, reporters, bloggers, and other members of the media who attend Events to report on proceedings, conduct interviews, and document activities.
              </p>
              <p className="mb-2">
                <strong>e) Governmental, Non-Governmental, and Associative Entities:</strong> Representatives or members from government organizations, associations, non-governmental organizations (NGOs), and other entities participating in Events for networking purposes, to represent their organizations, or to engage in collaborative activities.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">3. Attendance at Events</h2>
              <p className="mb-2">a) Registration confers the right to access pre-scheduled conferences, seminars, sessions, and networking activities, as outlined in the event program.</p>
              <p className="mb-2">b) Participation in business meetings is subject to the sole discretion of all parties involved. Submission of meeting preferences does not guarantee their fulfillment.</p>
              <p className="mb-2">c) The Organizer explicitly disclaims all liability for inaccuracies in provided information, changes in attendee lists, or missed appointments.</p>
              <p className="mb-2">d) Attendees are mandated to maintain the utmost professional conduct and to comply with all rules and regulations established by the Organizer and the Venue.</p>
              <p className="mb-2">e) All materials disseminated during Events are subject to intellectual property rights. Any unauthorized use is prohibited.</p>
              <p className="mb-2">f) The Organizer disclaims any representations or warranties regarding the number of attendees, the identity or qualifications of speakers, or the veracity of Event content. The Organizer will not be held liable for any dissatisfaction, misinformation, or alterations to Event details.</p>
              <p className="mb-2">g) Attendees are singularly responsible for the safety and security of their personal belongings. The Organizer disclaims any liability for the loss, damage, or theft of any personal items brought to Events.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">4. Modifications by the Organizer</h2>
              <p className="mb-2">a) The Organizer reserves the right to postpone, amend, or alter Event timing, location, or content should circumstances arise rendering the Event commercially unviable. No refunds shall be issued under such circumstances.</p>
              <p className="mb-2">b) Access to Events is contingent upon full payment of the Fee. The Organizer reserves the right to withhold information or deny entry in the case of non-payment.</p>
              <p className="mb-2">c) The Organizer may alter the Venue or cancel Events, subject to timely notification to Attendees. In the event of cancellations by the Organizer, Events may be rescheduled within a 12-month period, without entitlement to refunds.</p>
              <p className="mb-2">d) The Organizer reserves the right to cancel any bookings and shall issue a full refund of fees paid.</p>
              <p className="mb-2">e) The Organizer shall not be liable for any indirect costs (including travel and accommodation expenses) incurred by Attendees due to alterations in the Event schedule.</p>
              <p className="mb-2">f) The Organizer disclaims responsibility for the condition of venue facilities, any property damage, or personal injuries sustained during Events.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">5. Force Majeure</h2>
              <p>
                The Organizer shall be exempt from any liability for the failure to fulfill its obligations in cases where such failure is the result of any cause beyond reasonable control, including mechanical, electronic, or communications failure, acts of God, natural disasters, pandemics, perceived risks to health and safety, acts of terrorism, or governmental actions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">6. Compliance with Laws and Regulations</h2>
              <p>
                Attendees commit to compliance with all applicable laws, regulations, and ordinances throughout the duration of Events.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">7. Anti-Corruption and Bribery</h2>
              <p>
                All parties involved in Events are duty-bound to adhere to applicable anti-corruption and anti-bribery laws and shall conduct all activities in an ethical and lawful manner.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">8. Code of Conduct</h2>
              <p>
                All Attendees are subject to a <Link to="/code-of-conduct" className="text-[#C8A951] hover:underline">Code of Conduct</Link> which delineates behavioral expectations, including policies on harassment, discrimination, and other forms of misconduct.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">9. Photography and Recording Policy</h2>
              <p>
                Policies governing photography, video recording, and audio recording are regulated by specific guidelines set forth by the Organizer. Attendees will be subject to consent requirements for the utilization of their image or likeness in materials arising from Events.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">10. Limitation of Liability for Third-Party Actions</h2>
              <p>
                The Organizer disclaims any liability for the actions or omissions of third-party service providers, vendors, or partners associated with Events.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">11. Governing Law and Jurisdiction</h2>
              <p>
                This Agreement shall be governed by and construed in accordance with the laws of Singapore.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">12. Intellectual Property of Event Content</h2>
              <p>
                All intellectual property rights in the content presented at Events shall remain the exclusive property of their respective owners. The usage of such content by Attendees is subject to limitations as established by the Organizer or the content owner.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">13. Insurance Requirements</h2>
              <p>
                Exhibitors, sponsors, and certain other participants may be required to maintain adequate insurance coverage.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">14. Financial Obligations and Cancellations by the Attendee</h2>
              <p className="mb-2">a) The Total Fees shall be paid as specified in the registration process.</p>
              <p className="mb-2">b) Discounts (Early Bird or Special Group) necessitate payment before the stipulated cut-off date.</p>
              <p className="mb-2">c) Attendees are exclusively accountable for their accommodation, travel, and other personal expenses.</p>
              <p className="mb-2">d) Attendee bookings are transferable but non-cancellable. Any substitute Attendee must be approved by the Organizer and must assent to these Terms.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">15. Sponsors and Exhibitors</h2>
              <p>
                In the event of cancellation initiated by the Organizer, alternative opportunities shall be extended or a complete refund shall be issued. Refunds shall not be extended for cancellations initiated by Exhibitors or Sponsors.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">16. General Provisions</h2>
              <p className="mb-2">a) This Agreement constitutes the entire agreement between the Parties and supersedes all prior understandings or agreements.</p>
              <p className="mb-2">b) Nothing in this Agreement excludes or limits liability for fraud or for any injury or loss caused by the Organizer's negligence.</p>
              <p className="mb-2">c) The Organizer's total liability under this Agreement is strictly limited to the fees paid by the Attendee.</p>
              <p className="mb-2">d) The rights of the Attendee under this Agreement are non-transferable.</p>
              <p className="mb-2">e) All correspondence relating to this Agreement must be in written form.</p>
              <p className="mb-2">f) Any disputes arising under this Agreement shall be subject to binding arbitration in Singapore, in accordance with Singapore law.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">17. Data Protection</h2>
              <p className="mb-2">a) Personal data provided by Attendees shall be treated with the utmost confidentiality and utilized in accordance with applicable data protection laws and our Privacy Policy. This data may be shared within our Group and with external partners unless an opt-out is explicitly communicated by the Attendee.</p>
              <p className="mb-2">b) Personal data collected from Attendees shall be retained solely for the duration necessary for the purposes elucidated and in strict compliance with applicable data protection laws.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">18. Dispute Resolution Mechanism</h2>
              <p>
                In the event of a dispute, the Parties commit to engaging in good-faith negotiations for a period of 30 days. In the event that the dispute remains unresolved, it shall be referred to binding arbitration in Singapore, in accordance with Singapore law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">19. Data Security and Cyber Liability</h2>
              <p>
                The Organizer pledges to maintain the utmost security of collected data. In the event of a data breach or cyber-attack, liability shall be assessed in accordance with relevant laws and regulations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">20. Cancellation by Attendee</h2>
              <p>
                Attendees retain the prerogative to cancel their participation, subject to the conditions enumerated in our Cancellation Policy, which may entail applicable penalties or partial refunds.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">21. Indemnification Clause</h2>
              <p>
                Attendees agree to indemnify the Organizer against all claims arising from damages caused by the Attendees.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">22. Compliance with Health and Safety Regulations</h2>
              <p>
                Attendees shall comply with all health and safety regulations as mandated by the Venue, local authorities, or the Organizer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">23. Alterations to the Agenda or Speakers</h2>
              <p>
                The Organizer reserves the right to effect alterations to the agenda or speakers and shall inform Attendees of any substantial changes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">24. Specific Terms for Networking or Matchmaking Services</h2>
              <p>
                Attendees engaging in networking and matchmaking services acknowledge and consent that the Organizer shall not be held responsible for any disputes or disagreements that may arise between Attendees who connect via these services. The Organizer provides these services exclusively as a facilitator, and any connections formed are undertaken at the sole discretion and risk of the Attendees.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">25. Limitations on Marketing Activities by Attendees</h2>
              <p>
                Marketing activities undertaken by Attendees, especially Exhibitors and Sponsors, shall be subject to guidelines set by the Organizer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">26. Right to Refuse Admission</h2>
              <p>
                The Organizer reserves the right to refuse admission or eject Attendees for non-compliance with the <Link to="/code-of-conduct" className="text-[#C8A951] hover:underline">Code of Conduct</Link>, or if the Attendee is deemed a commercial risk to the Event.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">27. Amendments to Terms and Conditions</h2>
              <p>
                The Organizer reserves the right to amend these Terms & Conditions at any time.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">28. Severability Clause</h2>
              <p>
                In the event that any portion of this Agreement is determined to be unenforceable, the remainder of the Agreement shall remain valid and enforceable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">29. Non-Discrimination Clause</h2>
              <p>
                The Organizer is committed to providing an inclusive and respectful environment for all Attendees. Discrimination of any kind based on race, color, religion, gender, national origin, age, disability, or any other protected characteristic is prohibited.
              </p>
            </section>

            <div className="mt-12 pt-8 border-t border-[#E2E8F0]">
              <p className="text-sm text-[#64748B]">
                IQ International Pte. Ltd | Last updated: February 2026
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
